import { useData, useRuntime } from "../../../runtime/provider";
import Divider from "@components/Divider";
import GradientButton from "@components/GradientButton";
import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LScreen from "@components/LScreen";
import LScrollView from "@components/LScrollView";
import LText from "@components/LText";
import LinkButton from "@components/LinkButton";
import { Image } from "expo-image";
import React from "react";
import { theme } from "@theme";
import config from "@utils/config";

import useDeleteAccount from "@hooks/useDeleteAccount";
import { LogoutModalSWMSheet } from "@components/modals/LogoutModalSWM";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { router } from "expo-router";
import { useMe } from "@hooks/trpc/useMe";
import { useShare } from "@hooks/trpc/useShare";
import { isDebugMode, isDevEnv } from "@utils/envs";
import ProfileCard from "@components/Account/Settings/ProfileCard";
import Subscription from "@components/Account/Settings/Subscription";
import { SettingsFeatureFlags } from "@components/Account/Settings/SettingsFeatureFlags";
import useIsArtistManager from "@hooks/useIsArtistManager";
import { AccountDeleting } from "@components/Account/Settings/AccountDeleting";
import { captureException } from "@utils/captureException";

import ConnectedServices from "./ConnectedServices";
import { toast } from "sonner-native";
import { useAuth } from "@utils/firebaseAuth/auth";
import {
  hasPasswordProvider,
  PASSWORD_RESET_ACTION_LABEL,
} from "./passwordReset";

export function SettingsLissenerScreen() {
  const session = useData();
  const { kv } = useRuntime().ports;
  const {
    query: { data: user },
  } = useMe();
  const { deleteAccount, isDeleting } = useDeleteAccount(
    SettingsLissenerScreen.name,
  );
  const isArtistManager = useIsArtistManager();
  const share = useShare();
  const bottomSheetManager = useBottomSheetManager();
  const { firebaseUser } = useAuth();
  const canSendPasswordReset = hasPasswordProvider(firebaseUser?.providerData);

  const sendPasswordResetLink = async () => {
    const email = user?.email;
    if (!canSendPasswordReset || !email) return;

    try {
      await session.api.requestPasswordReset.mutate({
        email: email.trim(),
      });
      toast.success(`We've sent a password reset link to ${email}`);
    } catch (error) {
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "request-password-reset-settings-web",
      });
      // The API always returns success to prevent email enumeration,
      // but we still show a generic error if the request fails
      toast.error("Failed to send password reset email. Please try again.");
    }
  };

  const handlePressLogout = () => {
    bottomSheetManager.open(<LogoutModalSWMSheet />, {
      mode: "push",
    });
  };

  if (isDeleting) {
    return <AccountDeleting />;
  }

  return (
    <LScreen enableAutomaticTopPadding={true}>
      <LScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingVertical: theme.spacing.s2,
        }}
        showsVerticalScrollIndicator={false}
        bounces={false}
      >
        <ProfileCard />
        <Subscription />
        <ConnectedServices />
        <LText variant="bodyM2" color="listItemHeaderText" mt="m1" mb="s3">
          Actions
        </LText>
        <LBox gap="s3">
          {isArtistManager ? (
            <GradientButton
              sentryEventName="artist_settings"
              label="ARTIST SETTINGS"
              variant="tertiary"
              textVariant="bodyM2"
              onPress={() =>
                router.navigate(`/(app)/(tabs)/profile/artist/settings`)
              }
              gradient={[
                theme.colors.buttonBackgroundPrimaryLeft,
                theme.colors.buttonBackgroundPrimaryRight,
              ]}
              borderRadius={"max"}
            />
          ) : null}

          <GradientButton
            sentryEventName="invite_friend"
            label="INVITE A FRIEND"
            variant="tertiary"
            textVariant="bodyM2"
            onPress={async () => {
              await share.mutateAsync({
                linkType: "custom-action-link",
                type: "refer-friend",
                userId: user?.id,
              });
            }}
            gradient={[
              theme.colors.buttonBackgroundPrimaryLeft,
              theme.colors.buttonBackgroundPrimaryRight,
            ]}
            borderRadius={"max"}
            enabled={!share.isPending}
          />

          {canSendPasswordReset ? (
            <GradientButton
              sentryEventName="change_password"
              label={PASSWORD_RESET_ACTION_LABEL}
              accessibilityLabel={PASSWORD_RESET_ACTION_LABEL}
              variant="tertiary"
              textVariant="bodyM2"
              onPress={sendPasswordResetLink}
              gradient={[
                theme.colors.buttonBackgroundPrimaryLeft,
                theme.colors.buttonBackgroundPrimaryRight,
              ]}
              borderRadius={"max"}
              enabled={!!user?.email}
            />
          ) : null}
        </LBox>
        <Divider my="m1" />
        <LinkButton
          iconColor="#E6765A"
          sentryEventName="settings_privacy_policy"
          title="Privacy policy"
          iconName="link"
          mb="m1"
          onPress={() => window.open(config.PRIVACY_URL, "_blank")}
        />

        <LinkButton
          iconColor="#E6765A"
          sentryEventName="settings_terms_and_conditions"
          title="Terms and conditions"
          iconName="link"
          mb="m1"
          onPress={() => window.open(config.TERMS_URL, "_blank")}
        />
        <LButton
          label="LOG OUT"
          mb="m2"
          mt="l1"
          onPress={handlePressLogout}
          sentryEventName="logout"
          textVariant="bodyM2Transparent"
          borderRadius={"max"}
        />
        <LButton
          sentryEventName="delete_account"
          label="DELETE MY ACCOUNT"
          variant="tertiary-destructive"
          textVariant="bodyM2"
          onPress={deleteAccount}
        />

        {isDebugMode() || isDevEnv() ? (
          <LBox
            borderColor="white"
            borderWidth={1}
            marginVertical="m1"
            paddingHorizontal="m1"
            paddingVertical="s4"
            gap="s3"
          >
            <LText textAlign="center">DEV MENU</LText>
            <LButton
              variant="primary"
              label="Clear expo image cache"
              sentryEventName="clear_expo_image_cache"
              onPress={async () => {
                await Image.clearMemoryCache();
                await Image.clearDiskCache();
              }}
            />
            <LButton
              variant="primary"
              label="Clear react-query cache"
              sentryEventName="clear_react_query_cache"
              onPress={async () => {
                session.queryClient.clear();
              }}
            />
            <LButton
              variant="primary"
              label="Clear mmkv"
              sentryEventName="clear_mmkv"
              onPress={async () => {
                kv.clearAll();
              }}
            />
            <SettingsFeatureFlags />
          </LBox>
        ) : null}
      </LScrollView>
    </LScreen>
  );
}

export default SettingsLissenerScreen;
